import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { EnvelopeIcon, ClockIcon, CheckIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface ContactFormData {
  name: string;
  email: string;
  company: string;
  subject: string;
  message: string;
}

interface ContactType {
  type: string;
  title: string;
  subtitle: string;
  subject: string;
  prefillMessage: string;
  guidance: string[];
  estimatedResponse: string;
  nextSteps: string[];
}

const ContactPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contactType, setContactType] = useState<ContactType | null>(null);
  
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<ContactFormData>();

  // Define contact types and their configurations
  const contactTypes: Record<string, ContactType> = useMemo(() => ({
    'custom-reports': {
      type: 'custom-reports',
      title: 'Request Custom Report',
      subtitle: 'Get a tailored trend analysis report for your specific needs',
      subject: 'Custom Report Request',
      prefillMessage: `Hi TrendBeat team,

I'm interested in a custom trend analysis report for my business. 

My requirements:
- Industry: [Please specify your industry]
- Specific trends or topics: [What trends are you interested in?]
- Timeline: [When do you need this by?]
- Budget range: [£500-2000 per report]

Please let me know what information you need from me to get started.

Best regards,
[Your name]`,
      guidance: [
        'Be specific about your industry and target audience',
        'Include any particular trends or topics you want to analyse',
        'Mention your timeline and budget expectations',
        'Share any specific business challenges you\'re trying to solve'
      ],
      estimatedResponse: 'Within 2 business days',
      nextSteps: [
        'We\'ll schedule a discovery call to understand your needs',
        'Provide a detailed proposal with timeline and pricing',
        'Begin research and analysis phase',
        'Deliver your custom report within 5-10 business days'
      ]
    },
    'consulting': {
      type: 'consulting',
      title: 'Book Consultation',
      subtitle: 'Get expert guidance on trend strategy from our specialists',
      subject: 'Consulting Session Request',
      prefillMessage: `Hi TrendBeat team,

I'd like to book a consultation session to discuss trend strategy for my business.

About my business:
- Industry: [Your industry]
- Company size: [Number of employees/revenue range]
- Current challenges: [What are you trying to achieve?]
- Preferred session length: [1-2 hours]

I'm particularly interested in:
- [Specific areas you want to discuss]

Please let me know your availability and what I should prepare for our session.

Best regards,
[Your name]`,
      guidance: [
        'Describe your business and current challenges',
        'Specify what type of guidance you need',
        'Include your preferred session length and timing',
        'Mention any specific trends or markets you want to explore'
      ],
      estimatedResponse: 'Within 24 hours',
      nextSteps: [
        'We\'ll confirm your consultation slot within 24 hours',
        'Send you a pre-session questionnaire',
        'Conduct the consultation session (£150-300/hour)',
        'Provide follow-up recommendations and resources'
      ]
    },
    'training': {
      type: 'training',
      title: 'Schedule Training',
      subtitle: 'Build your team\'s trend analysis capabilities',
      subject: 'Training Workshop Request',
      prefillMessage: `Hi TrendBeat team,

I'm interested in scheduling a training workshop for my team to build trend analysis capabilities.

Training details:
- Team size: [Number of participants]
- Department: [Marketing, Product, Strategy, etc.]
- Current skill level: [Beginner, Intermediate, Advanced]
- Preferred format: [In-person, Virtual, Hybrid]
- Timeline: [When would you like to schedule this?]

Specific areas we want to cover:
- [List specific skills or topics]

Please provide information about your training programmes and pricing.

Best regards,
[Your name]`,
      guidance: [
        'Specify your team size and department',
        'Describe current skill levels and learning objectives',
        'Include preferred training format and timeline',
        'Mention any specific tools or methodologies you want to learn'
      ],
      estimatedResponse: 'Within 2 business days',
      nextSteps: [
        'We\'ll design a customised training programme',
        'Provide detailed proposal with pricing (£2000-5000)',
        'Schedule the workshop at your preferred time',
        'Deliver training with follow-up support and resources'
      ]
    },
    'data-exports': {
      type: 'data-exports',
      title: 'Request Data Export',
      subtitle: 'Get historical trend data for your analysis needs',
      subject: 'Data Export Request',
      prefillMessage: `Hi TrendBeat team,

I need historical trend data for my research/analysis project.

Export requirements:
- Data period: [Date range you need]
- Trends/categories: [Specific trends or categories]
- Export format: [CSV, JSON, Excel]
- Data granularity: [Daily, Weekly, Monthly]
- Specific filters: [Any particular criteria]

Use case:
        - [Brief description of how you'll use the data]

Please let me know pricing and delivery timeline.

Best regards,
[Your name]`,
      guidance: [
        'Specify the exact date range you need',
        'Include specific trends or categories of interest',
        'Mention your preferred export format',
        'Describe how you plan to use the data'
      ],
      estimatedResponse: 'Within 24 hours',
      nextSteps: [
        "We'll confirm data availability and pricing (£100-500)",
        'Process your custom data export',
        'Deliver within 24-48 hours',
        'Provide data documentation and support'
      ]
    },
    'api-access': {
      type: 'api-access',
      title: 'Get API Access',
      subtitle: 'Integrate trend data into your applications',
      subject: 'API Access Request',
      prefillMessage: `Hi TrendBeat team,

I'm interested in API access for my application development.

Technical requirements:
- Expected request volume: [Requests per month]
- Use case: [What are you building?]
- Integration type: [Web app, Mobile app, Dashboard, etc.]
- Required endpoints: [Specific data you need]
- Timeline: [When do you need access?]

Technical details:
- [Any specific technical requirements or constraints]

Please provide information about API pricing and documentation.

Best regards,
[Your name]`,
      guidance: [
        'Describe your application and use case',
        'Specify expected API request volume',
        'Include technical requirements and timeline',
        'Mention any specific data endpoints you need'
      ],
      estimatedResponse: 'Within 24 hours',
      nextSteps: [
        "We'll provide API documentation and pricing (£50-200/month)",
        'Set up your API access and rate limits',
        'Provide technical support and integration guidance',
        'Monitor usage and provide ongoing support'
      ]
    },
    'custom': {
      type: 'custom',
      title: 'Custom Solution',
      subtitle: 'Custom solutions for large organisations',
      subject: 'Custom Solution Request',
      prefillMessage: `Hi TrendBeat team,

I'm interested in exploring custom solutions for our organisation.

Company information:
- Company size: [Number of employees]
- Industry: [Your industry]
- Current challenges: [What problems are you trying to solve?]
- Budget range: [Custom pricing]

Specific requirements:
- [List your specific needs and requirements]
- [Any custom integrations or features needed]
- [Timeline for implementation]

Please arrange a consultation to discuss our custom needs.

Best regards,
[Your name]`,
      guidance: [
        'Provide company size and industry information',
        'Describe your specific business challenges',
        'Include budget expectations and timeline',
        'Mention any custom requirements or integrations'
      ],
      estimatedResponse: 'Within 4 hours',
      nextSteps: [
        'We\'ll schedule a call with our custom solutions team within 4 hours',
        'Conduct a detailed requirements assessment',
        'Provide custom proposal with pricing and timeline',
        'Begin implementation with dedicated account manager'
      ]
    }
  }), []);

  // Set up contact type based on URL parameters
  useEffect(() => {
    const type = searchParams.get('type');
    if (type && contactTypes[type]) {
      setContactType(contactTypes[type]);
      setValue('subject', contactTypes[type].subject);
      setValue('message', contactTypes[type].prefillMessage);
    }
  }, [searchParams, setValue, contactTypes]);

  const subjects = [
    { value: 'support', label: 'Support' },
    { value: 'sales', label: 'Sales' },
    { value: 'general', label: 'General Inquiry' },
    { value: 'partnership', label: 'Partnership' },
    { value: 'feedback', label: 'Feedback' },
    { value: 'custom-reports', label: 'Custom Reports' },
    { value: 'consulting', label: 'Consulting' },
    { value: 'training', label: 'Training' },
    { value: 'data-exports', label: 'Data Exports' },
    { value: 'api-access', label: 'API Access' },
    { value: 'custom', label: 'Custom Solution' },
  ];

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const responseTime = contactType?.estimatedResponse || 'within 24 hours';
      toast.success(`Message sent successfully! We'll get back to you ${responseTime}.`);
      reset();
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-purple-50 to-indigo-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
          >
            {contactType ? contactType.title : 'Get in Touch'}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl text-gray-600 max-w-2xl mx-auto"
          >
            {contactType ? contactType.subtitle : 'Have questions? We\'d love to hear from you. Send us a message and we\'ll respond as soon as possible.'}
          </motion.p>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {contactType ? 'Tell us about your needs' : 'Send us a message'}
              </h2>
              
              {contactType && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h3 className="text-sm font-semibold text-blue-900 mb-2">What to include:</h3>
                  <ul className="space-y-1">
                    {contactType.guidance.map((item, index) => (
                      <li key={index} className="flex items-start text-sm text-blue-800">
                        <CheckIcon className="h-4 w-4 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      {...register('name', { required: 'Name is required' })}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                        errors.name ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Your name"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      {...register('email', { 
                        required: 'Email is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address'
                        }
                      })}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                        errors.email ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="your@email.com"
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                    Company
                  </label>
                  <input
                    type="text"
                    id="company"
                    {...register('company')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Your company (optional)"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                    Subject *
                  </label>
                  <select
                    id="subject"
                    {...register('subject', { required: 'Subject is required' })}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                      errors.subject ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select a subject</option>
                    {subjects.map((subject) => (
                      <option key={subject.value} value={subject.value}>
                        {subject.label}
                      </option>
                    ))}
                  </select>
                  {errors.subject && (
                    <p className="mt-1 text-sm text-red-600">{errors.subject.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    rows={8}
                    {...register('message', { required: 'Message is required' })}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                      errors.message ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Tell us how we can help you..."
                  />
                  {errors.message && (
                    <p className="mt-1 text-sm text-red-600">{errors.message.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-purple-600 text-white py-3 px-6 rounded-md font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </motion.div>

            {/* Contact Info & Next Steps */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {contactType ? (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">What happens next?</h2>
                  
                  <div className="space-y-6">
                    <div className="p-6 bg-green-50 border border-green-200 rounded-lg">
                      <h3 className="text-lg font-semibold text-green-900 mb-3">Response Time</h3>
                      <p className="text-green-800 font-medium">{contactType.estimatedResponse}</p>
                    </div>

                    <div className="p-6 bg-purple-50 border border-purple-200 rounded-lg">
                      <h3 className="text-lg font-semibold text-purple-900 mb-3">Next Steps</h3>
                      <ol className="space-y-2">
                        {contactType.nextSteps.map((step, index) => (
                          <li key={index} className="flex items-start text-sm text-purple-800">
                            <span className="flex items-center justify-center w-6 h-6 bg-purple-200 text-purple-700 rounded-full text-xs font-medium mr-3 flex-shrink-0">
                              {index + 1}
                            </span>
                            {step}
                          </li>
                        ))}
                      </ol>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h2>
                  
                  <div className="space-y-8">
                    <div className="flex items-start">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                        <EnvelopeIcon className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Email</h3>
                        <p className="text-gray-600">support@trendbeat.twokai.com</p>
                        <p className="text-sm text-gray-500 mt-1">We\'ll get back within 24 hours</p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                        <ClockIcon className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Office Hours</h3>
                        <p className="text-gray-600">Monday - Friday: 9:00 AM - 6:00 PM GMT</p>
                        <p className="text-gray-600">Saturday: 10:00 AM - 4:00 PM GMT</p>
                        <p className="text-sm text-gray-500 mt-1">Closed on Sundays and holidays</p>
                      </div>
                    </div>

                  </div>

                  <div className="mt-12 p-6 bg-gray-50 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Before you contact us</h3>
                    <p className="text-gray-600 mb-4">
                      Check our FAQ first - you might find the answer you\'re looking for right away.
                    </p>
                    <button
                      type="button"
                      className="text-purple-600 hover:text-purple-700 font-medium"
                      onClick={() => window.open('/faq', '_blank')}
                    >
                      View FAQ →
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage; 